// ======================================================================
// File: businessPartnerService.js
// Purpose:
//   Business Partner (Vendor / Customer / Transporter / Job Worker)
//   Frontend API client aligned EXACTLY with backend routes
//
// FIX LOG:
// ----------------------------------------------------------------------
// 2026-01-31 10:40 IST
// - FIXED: frontend → backend payload mismatch
// - name          → bp_name
// - partner_type  → bp_type
// - status handled as is_active
// - prevents: "Missing required field(s): bp_name"
//
// 2026-01-31 14:15 IST
// - ✅ FIXED: edit drawer empty issue
// - ✅ Added backend → UI reverse mapper
// - ✅ No breaking changes
// ======================================================================

import axiosClient from "../../../../../../api/axiosClient";

/**
 * ----------------------------------------------------------------------
 * BASE PATH (BACKEND ALIGNED)
 * ----------------------------------------------------------------------
 */
const BASE_URL = "/api/ppe/masters/business-partner";

/**
 * ----------------------------------------------------------------------
 * INTERNAL PAYLOAD MAPPER
 * UI → BACKEND
 * ----------------------------------------------------------------------
 */
function mapToBackendPayload(form) {
  return {
    // ✅ support both create + edit payloads
    bp_name: form.bp_name || form.name,
    bp_type: form.bp_type || form.partner_type,

    roles: form.roles || [],
    addresses: form.addresses || [],
    contacts: form.contacts || [],
    taxes: form.taxes || [],
    banks: form.banks || [],
  };
}

/**
 * ----------------------------------------------------------------------
 * INTERNAL PAYLOAD MAPPER
 * BACKEND → UI
 * ----------------------------------------------------------------------
 */
function mapFromBackendToForm(data) {
  return {
    id: data.id,
    name: data.bp_name,
    partner_type: data.bp_type,

    roles: data.roles || [],
    addresses: data.addresses || [],

    // ✅ FIX CONTACT LOAD
    contacts: (data.contacts || []).map((c) => ({
      id: c.id,
      name: c.contact_name,
      contact_name: c.contact_name,
      designation: c.designation,
      department: c.department,
      mobile: c.mobile,
      email: c.email,
      is_primary: c.is_default,
      is_default: c.is_default,
      is_active: c.is_active,
    })),

    taxes: data.taxes || [],
    banks: data.banks || [],
  };
}


/**
 * ----------------------------------------------------------------------
 * LIST BUSINESS PARTNERS
 * Backend: GET /api/ppe/masters/business-partner
 * ----------------------------------------------------------------------
 */
async function listBusinessPartners(params = {}) {
  const res = await axiosClient.get(
    BASE_URL,
    { params }
  );

  const payload = res.data?.data || {};

  return {
    items: payload.items || [],
    total: payload.total || 0,
    page: payload.page || 1,
    page_size:
      payload.page_size || params.page_size || 10,
  };
}

/**
 * ----------------------------------------------------------------------
 * KPI COUNTS
 * Backend: GET /api/ppe/masters/business-partner/kpis
 * ----------------------------------------------------------------------
 */
async function getBusinessPartnerKpis() {
  const res = await axiosClient.get(
    `${BASE_URL}/kpis`
  );
  return res.data?.data || {};
}

/**
 * ----------------------------------------------------------------------
 * CREATE BUSINESS PARTNER
 * Backend: POST /api/ppe/masters/business-partner
 * ----------------------------------------------------------------------
 */
async function createBusinessPartner(payload) {
  const res = await axiosClient.post(
    BASE_URL,
    mapToBackendPayload(payload)
  );
  return res.data?.data;
}

/**
 * ----------------------------------------------------------------------
 * UPDATE BUSINESS PARTNER
 * Backend: PUT /api/ppe/masters/business-partner/:id
 * ----------------------------------------------------------------------
 */
async function updateBusinessPartner(id, payload) {
  const res = await axiosClient.put(
    `${BASE_URL}/${id}`,
    mapToBackendPayload(payload)
  );
  return res.data?.data;
}

/**
 * ----------------------------------------------------------------------
 * VIEW SINGLE BUSINESS PARTNER
 * Backend: GET /api/ppe/masters/business-partner/:id
 * ----------------------------------------------------------------------
 */
async function getBusinessPartner(id) {
  const res = await axiosClient.get(
    `${BASE_URL}/${id}`
  );

  const data = res.data?.data || {};

  // ✅ THIS FIXES EMPTY EDIT DRAWER
  return mapFromBackendToForm(data);
}

/**
 * ----------------------------------------------------------------------
 * ACTIVATE
 * Backend: PUT /api/ppe/masters/business-partner/status
 * ----------------------------------------------------------------------
 */
async function activateBusinessPartner(id) {
  const res = await axiosClient.put(
    `${BASE_URL}/status`,
    {
      bp_id: id,
      is_active: true,
    }
  );
  return res.data?.data;
}

/**
 * ----------------------------------------------------------------------
 * DEACTIVATE
 * Backend: PUT /api/ppe/masters/business-partner/status
 * ----------------------------------------------------------------------
 */
async function deactivateBusinessPartner(id) {
  const res = await axiosClient.put(
    `${BASE_URL}/status`,
    {
      bp_id: id,
      is_active: false,
    }
  );
  return res.data?.data;
}

/**
 * ----------------------------------------------------------------------
 * LIST ONLY VENDOR PARTNERS
 * Backend: GET /api/ppe/masters/business-partner/vendors
 * ----------------------------------------------------------------------
 */
async function getVendorPartners() {
  const res = await axiosClient.get(`${BASE_URL}/vendors`);
  return res.data?.data || [];
}

/**
 * ----------------------------------------------------------------------
 * CUSTOMER LOOKUP
 * Backend:
 * GET /api/ppe/masters/business-partner/customers/lookup
 * ----------------------------------------------------------------------
 */
async function getCustomerLookup(
  search = ""
) {

  const res =
    await axiosClient.get(

      `${BASE_URL}/customers/lookup`,

      {
        params: {
          search,
        },
      }

    );

  return (
    res.data?.data?.items || []
  );

}

/**
 * ----------------------------------------------------------------------
 * EXPORT
 * ----------------------------------------------------------------------
 */
const businessPartnerService = {
  listBusinessPartners,
  getBusinessPartnerKpis,
  createBusinessPartner,
  updateBusinessPartner,
  getBusinessPartner,
  activateBusinessPartner,
  deactivateBusinessPartner,
  getVendorPartners,
  getCustomerLookup,
};

export default businessPartnerService;
